import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import UnpublishDialog from './UnpublishDialog';

const SiteCard = ({ site, onDelete, onDuplicate, onUnpublish }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [showUnpublishDialog, setShowUnpublishDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const menuRef = useRef(null);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Close dialogs on Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (showDeleteDialog) setShowDeleteDialog(false);
        if (showDuplicateDialog) setShowDuplicateDialog(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showDeleteDialog, showDuplicateDialog]);

  const getStatusConfig = (status) => {
    switch (status) {
      case 'published':
        return {
          label: 'Опубликован',
          color: 'text-success',
          dot: 'bg-success'
        };
      case 'modified':
        return {
          label: 'Есть изменения',
          color: 'text-warning',
          dot: 'bg-warning'
        };
      case 'draft':
      default:
        return {
          label: 'Черновик',
          color: 'text-text-secondary',
          dot: 'bg-text-secondary/50'
        };
    }
  };

  const statusConfig = getStatusConfig(site?.status);

  // Build site URL
  const baseDomain = import.meta.env.VITE_BASE_DOMAIN || 'simplecms.ru';
  const siteUrl = site?.customDomain
    ? site.customDomain
    : site?.subdomain
      ? `${site.subdomain}.${baseDomain}`
      : null;

  const handleCardClick = () => {
    navigate(`/projects/${site?.id}/editor`);
  };

  const handleOpenSite = (e) => {
    e.stopPropagation();
    if (siteUrl) {
      window.open(`https://${siteUrl}`, '_blank');
    }
  };

  const handleMenuClick = (e) => {
    e.stopPropagation();
    setMenuOpen(!menuOpen);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    navigate(`/projects/${site?.id}/editor`);
  };

  const handlePreview = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    navigate(`/projects/${site?.id}/preview`);
  };

  const handleSettings = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    navigate(`/projects/${site?.id}/settings`);
  };

  const handleDuplicateClick = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    setShowDuplicateDialog(true);
  };

  const handleDuplicateConfirm = async () => {
    if (onDuplicate && !isDuplicating) {
      setIsDuplicating(true);
      try {
        await onDuplicate(site?.id);
        setShowDuplicateDialog(false);
      } finally {
        setIsDuplicating(false);
      }
    }
  };

  const handleUnpublishClick = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    setShowUnpublishDialog(true);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (onDelete && !isDeleting) {
      setIsDeleting(true);
      try {
        await onDelete(site?.id);
      } finally {
        setIsDeleting(false);
        setShowDeleteDialog(false);
      }
    }
  };

  return (
    <>
      <article
        onClick={handleCardClick}
        className="group bg-card rounded-xl border border-border overflow-hidden cursor-pointer transition-all hover:shadow-lg hover:border-primary/30"
        role="button"
        tabIndex={0}
        aria-label={`Открыть сайт ${site?.name}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCardClick();
          }
        }}
      >
        {/* Thumbnail */}
        <div className="relative aspect-video bg-muted">
          {site?.thumbnail ? (
            <img
              src={site.thumbnail}
              alt={site?.name}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-muted to-muted/50">
              <Icon name="Globe" size={32} className="text-text-secondary/30 mb-1" />
              <span className="text-xs text-text-secondary/50">Нет превью</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Title */}
          <h3 className="font-semibold text-foreground mb-1.5 line-clamp-1 group-hover:text-primary transition-colors">
            {site?.name}
          </h3>

          {/* Status + URL */}
          <div className="flex items-center gap-2 text-sm mb-2">
            <span className={`flex items-center gap-1.5 ${statusConfig.color}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot}`} />
              {statusConfig.label}
            </span>
            {siteUrl && site?.status !== 'draft' && (
              <>
                <span className="text-text-secondary">·</span>
                <button
                  onClick={handleOpenSite}
                  className="text-text-secondary hover:text-primary truncate max-w-[140px] transition-colors"
                  title={siteUrl}
                >
                  {siteUrl}
                </button>
              </>
            )}
          </div>

          {/* Footer: time + menu */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-text-secondary">
              {site?.updatedAtFormatted ? `Изменён ${site.updatedAtFormatted}` : ''}
            </span>

            {/* Menu button */}
            <div ref={menuRef} className="relative">
              <button
                onClick={handleMenuClick}
                className={`flex items-center justify-center w-8 h-8 rounded-lg border transition-colors ${
                  menuOpen
                    ? 'bg-primary/10 border-primary/30 text-primary'
                    : 'bg-muted/50 border-border hover:bg-muted hover:border-border text-text-secondary'
                }`}
                title="Действия"
              >
                <Icon name="MoreHorizontal" size={18} />
              </button>

              {/* Dropdown menu */}
              {menuOpen && (
                <div className="absolute right-0 bottom-full mb-1 w-48 bg-card rounded-xl border border-border shadow-lg z-20 py-1 overflow-hidden">
                  <button
                    onClick={handleEdit}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                  >
                    <Icon name="Edit" size={16} />
                    Редактировать
                  </button>
                  <button
                    onClick={handlePreview}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                  >
                    <Icon name="Eye" size={16} />
                    Просмотр
                  </button>
                  {siteUrl && (
                    <button
                      onClick={handleOpenSite}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                    >
                      <Icon name="ExternalLink" size={16} />
                      Открыть сайт
                    </button>
                  )}
                  <button
                    onClick={handleSettings}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                  >
                    <Icon name="Settings" size={16} />
                    Настройки
                  </button>
                  <div className="border-t border-border my-1" />
                  {site?.status !== 'draft' && onUnpublish && (
                    <button
                      onClick={handleUnpublishClick}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                    >
                      <Icon name="EyeOff" size={16} />
                      Снять с публикации
                    </button>
                  )}
                  <button
                    onClick={handleDuplicateClick}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                  >
                    <Icon name="Copy" size={16} />
                    Дублировать
                  </button>
                  <button
                    onClick={handleDeleteClick}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-error hover:bg-muted transition-colors"
                  >
                    <Icon name="Trash2" size={16} />
                    Удалить
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </article>

      {showUnpublishDialog && (
        <UnpublishDialog
          site={site}
          siteUrl={siteUrl}
          onConfirm={onUnpublish}
          onClose={() => setShowUnpublishDialog(false)}
        />
      )}

      {/* Duplicate confirmation dialog */}
      {showDuplicateDialog && (
        <div
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4"
          onClick={() => !isDuplicating && setShowDuplicateDialog(false)}
        >
          <div
            className="bg-card rounded-2xl shadow-elevation-4 border border-border p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Copy" size={24} className="text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  Дублировать сайт?
                </h3>
                <p className="text-text-secondary">
                  Будет создана копия «{site?.name}» со всеми страницами и контентом.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDuplicateDialog(false)}
                disabled={isDuplicating}
                className="flex-1 px-4 py-2.5 border border-border rounded-xl text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50"
              >
                Отмена
              </button>
              <button
                onClick={handleDuplicateConfirm}
                disabled={isDuplicating}
                className="flex-1 px-4 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isDuplicating && (
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                )}
                {isDuplicating ? 'Копирование...' : 'Дублировать'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation dialog */}
      {showDeleteDialog && (
        <div
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4"
          onClick={() => !isDeleting && setShowDeleteDialog(false)}
        >
          <div
            className="bg-card rounded-2xl shadow-elevation-4 border border-border p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                <Icon name="AlertTriangle" size={24} className="text-destructive" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  Удалить сайт?
                </h3>
                <p className="text-text-secondary">
                  Вы уверены, что хотите удалить «{site?.name}»? Это действие нельзя отменить.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteDialog(false)}
                disabled={isDeleting}
                className="flex-1 px-4 py-2.5 border border-border rounded-xl text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50"
              >
                Отмена
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="flex-1 px-4 py-2.5 bg-destructive text-destructive-foreground rounded-xl text-sm font-medium hover:bg-destructive/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isDeleting && (
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                )}
                {isDeleting ? 'Удаление...' : 'Удалить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SiteCard;
